import { Form, Link, useRouteLoaderData, useSubmit } from '@remix-run/react'
import { useRef } from 'react'
import { type loader as rootLoader } from '#app/root.tsx'
import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/ui/components'
import { getUserImgSrc } from '#app/utils/misc.js'
import { usePermissions } from '#app/utils/permission-context.tsx'
import { useUser } from '#app/utils/user.js'

export const UserDropdown = () => {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const { isStripeEnabled, subscriptionsEnabled } =
		useRouteLoaderData<typeof rootLoader>('root')!
	const { hasPermission, canPerformAction, permissionChecker } =
		usePermissions()

	// Permission-based checks
	const canAccessDashboard =
		canPerformAction('nightclub', 'read') ||
		canPerformAction('nightclub', 'create')
	const canValidateTickets = hasPermission('update:own:ticket')
	const canAccessAffiliateLinks =
		hasPermission('read:own:affiliate_link') ||
		hasPermission('create:own:affiliate_link')

	// Platform admin checks
	permissionChecker?.isPlatformAdmin() || false
	const canAccessPlatformDashboard =
		permissionChecker?.canAccessPlatformDashboard() || false

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="secondary"
					className="flex items-center gap-2"
					aria-label="User menu"
				>
					<img
						className="h-8 w-8 rounded-full object-cover"
						alt={
							user.name
								? `${user.name}'s profile photo`
								: 'User’s profile photo'
						}
						src={getUserImgSrc(user.image?.id)}
						aria-hidden="true"
					/>
					<span className="text-body-sm font-bold">{user.name}</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					{canAccessPlatformDashboard ? (
						<>
							<DropdownMenuItem asChild>
								<Link prefetch="intent" to="/platform">
									Platform Dashboard
								</Link>
							</DropdownMenuItem>
							<DropdownMenuSeparator />
						</>
					) : null}
					{canAccessDashboard ? (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to="/dashboard">
								Dashboard
							</Link>
						</DropdownMenuItem>
					) : null}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/dashboard/tickets">
							My Tickets
						</Link>
					</DropdownMenuItem>
					{canAccessAffiliateLinks ? (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to="/dashboard/affiliate-links">
								My Affiliate Links
							</Link>
						</DropdownMenuItem>
					) : null}
					{canValidateTickets ? (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to="/validate-tickets">
								Validate Tickets
							</Link>
						</DropdownMenuItem>
					) : null}
					{isStripeEnabled && subscriptionsEnabled ? (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to={`/account`}>
								Account
							</Link>
						</DropdownMenuItem>
					) : null}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/settings/profile`}>
							Profile
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<button type="submit">Logout</button>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
